import cx from "classnames";

import { DashboardActions } from "./DashboardActions";
import { FieldsTable } from "./FieldsTable";
import { Flex, Text, Icon, TooltipWrapper, Pill } from "@theorgchartdev/storybook";
import { GetWSMgr } from "@/Utils/StateLib";
import { MetricsTable } from "./MetricsTable";
import { SharedUsersTable } from "./SharedUsersTable";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { ConflictDetectionAlert } from "@/appComponents/Blocks/Composites/ConflictDetectionAlert/ConflictDetectionAlert";
import React from "react";
import { appstr } from "@coreapp/utils/appstr";
import { enumPlanStatus } from "@coreapp/constants";
import { SubmissionStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import {
  getSubmissionPillColors,
  getSubmissionsIcon,
  getSubmissionStatusDisplayName
} from "@/Utils/Plan/PlanSubmissionsStatuses";
import { DefaultPlanObjective, PlanObjectiveColors } from "@/Utils/Plan/ObjectivesHandler";

export const Dashboard = () => {
  const { chartDocument } = useChartDocumentContext();
  const { planManager } = usePlanManagerContext();
  const wsMgr = GetWSMgr();
  const docMgr = wsMgr?.GetDocMgr();
  const planMgr = planManager.state;
  if (!planMgr || !planMgr.fPlanInfo) return null;

  const planStatus = planMgr?.GetPlanStatus();
  const showPlanIcon =
    planStatus === enumPlanStatus.archived || planStatus === enumPlanStatus.locked;

  let planName = docMgr?.GetFileName();
  const planDescription = planMgr?.GetPlanDescription();
  const planObjective = planMgr?.GetPlanObjective() ?? DefaultPlanObjective;

  const planApprovalsEnabled = planMgr?.GetApprovalSettings()?.enablePlanApprovals;
  const planSubmission = planMgr?.GetPlanSubmission();
  const submissionStatus = planSubmission?.status ?? SubmissionStatus.Draft;

  return (
    <Flex direction={"column"}>
      <div className={"sidebar-header-buttons"}>
        <Flex direction={"column"} gap={"8px"} width={"100%"}>
          <Flex gap={"12px"} width={"100%"} justifyContent={"space-between"}>
            <Flex justifyContent={"space-between"} alignItems={"flex-start"}>
              <Flex
                alignItems={"flex-start"}
                width={"100%"}
                justifyContent={"space-between"}
                gap={"8px"}
              >
                <TooltipWrapper tooltipProps={{ message: planName }}>
                  <Text size={"medium"} modifier={"medium"} className={"oc-text-truncate--2"}>
                    {planName}
                  </Text>
                </TooltipWrapper>
                {showPlanIcon && (
                  <TooltipWrapper
                    tooltipProps={{
                      title: appstr("This plan is locked and cannot be edited.", {
                        status: appstr(planStatus).toLowerCase()
                      }),
                      message:
                        planStatus === enumPlanStatus.locked
                          ? appstr(
                              "Plan owners can unlock the plan in the Plan Explorer by hovering over the plan"
                            )
                          : undefined
                    }}
                  >
                    <Icon
                      size={"medium-small"}
                      name={planStatus === enumPlanStatus.archived ? "box-archive" : "lock"}
                      color={"default"}
                    />
                  </TooltipWrapper>
                )}
              </Flex>
            </Flex>
            <DashboardActions planMgr={planMgr} wsMgr={wsMgr} />
          </Flex>
          {planDescription && (
            <Text size={"small"} className={"oc-text-truncate--2"}>
              {planDescription}
            </Text>
          )}
          <Flex gap="8px" padding="8px 0 0 0">
            {planApprovalsEnabled && (
              <Pill
                label={getSubmissionStatusDisplayName(submissionStatus)}
                color={getSubmissionPillColors(submissionStatus)}
                leftContent={<Icon {...getSubmissionsIcon(submissionStatus)} size={"small"} />}
              />
            )}
            <Pill label={appstr(planObjective)} color={PlanObjectiveColors[planObjective]} />
          </Flex>
          <ConflictDetectionAlert showButton />
        </Flex>
      </div>
      <Flex
        direction={"column"}
        className={cx("sidebar-content-item--divider", "oc-dashboard-content")}
      >
        <MetricsTable />
        <FieldsTable planMgr={planMgr} />
        <SharedUsersTable />
      </Flex>
    </Flex>
  );
};
